/**
 * CSV Import Service
 * Parses transaction history CSVs from Binance, Coinbase, Kraken, Crypto.com, and KuCoin
 */

import { callCoinGeckoAPI } from './api';

export type ExchangeType = 'binance' | 'coinbase' | 'kraken' | 'cryptocom' | 'kucoin' | 'auto';

export interface ParsedTransaction {
  date: string;           // ISO date string (YYYY-MM-DD)
  type: 'buy' | 'sell' | 'mining' | 'staking';
  symbol: string;         // Coin symbol (BTC, ETH, etc.)
  quantity: number;
  price: number;          // Price per unit in USD/EUR
  total?: number;         // Total transaction value
  fee?: number;
  note?: string;
  exchange: string;
  originalRow?: Record<string, string>;
}

export interface ImportResult {
  success: boolean;
  transactions: ParsedTransaction[];
  errors: string[];
  warnings: string[];
  exchange: ExchangeType;
  totalRows: number;
  importedRows: number;
  skippedRows: number;
}

/**
 * Parse CSV string into rows
 */
function parseCSV(csvContent: string): { headers: string[]; rows: Record<string, string>[] } {
  const lines = csvContent.trim().split(/\r?\n/);
  if (lines.length < 2) {
    return { headers: [], rows: [] };
  }

  // Parse headers (handle quoted values)
  const parseRow = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());

    return result;
  };

  const headers = parseRow(lines[0]).map(h => h.toLowerCase().replace(/["\s]/g, '_'));
  const rows: Record<string, string>[] = [];

  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue;

    const values = parseRow(lines[i]);
    const row: Record<string, string> = {};

    headers.forEach((header, index) => {
      row[header] = values[index]?.replace(/^"|"$/g, '') || '';
    });

    rows.push(row);
  }

  return { headers, rows };
}

/**
 * Detect exchange type from CSV headers
 */
function detectExchange(headers: string[]): ExchangeType {
  const headerStr = headers.join(',').toLowerCase();

  // Binance: UTC_Time, Account, Operation, Coin, Change, Remark
  if (headerStr.includes('utc_time') && headerStr.includes('operation') && headerStr.includes('coin')) {
    return 'binance';
  }

  // Coinbase: Timestamp, Transaction Type, Asset, Quantity Transacted
  if (headerStr.includes('transaction_type') && headerStr.includes('asset') && headerStr.includes('quantity_transacted')) {
    return 'coinbase';
  }

  // Kraken: txid, refid, time, type, subtype, aclass, asset, amount, fee, balance
  if (headerStr.includes('txid') && headerStr.includes('refid') && headerStr.includes('aclass')) {
    return 'kraken';
  }

  // Crypto.com: Timestamp Description, Currency, Amount, To Currency, To Amount, Native Currency
  if (headerStr.includes('timestamp_description') ||
      (headerStr.includes('timestamp') && headerStr.includes('transaction_description') && headerStr.includes('native_currency'))) {
    return 'cryptocom';
  }

  // Alternative Crypto.com format: Timestamp (UTC), Transaction Description, Currency, Amount
  if (headerStr.includes('timestamp_(utc)') && headerStr.includes('transaction_description')) {
    return 'cryptocom';
  }

  // KuCoin: Time, Trading pair, Side, Price, Amount, Funds, Fee
  if ((headerStr.includes('trading_pair') || headerStr.includes('symbol')) &&
      headerStr.includes('side') && headerStr.includes('funds')) {
    return 'kucoin';
  }

  // Alternative KuCoin format: Time, Pair, Type, Side, Average Price
  if (headerStr.includes('pair') && headerStr.includes('side') && headerStr.includes('average_price')) {
    return 'kucoin';
  }

  return 'auto';
}

/**
 * Normalize coin symbol (remove prefixes like X, Z for Kraken)
 */
function normalizeSymbol(symbol: string): string {
  // Remove common prefixes
  let normalized = symbol.toUpperCase().trim();

  // Kraken uses X/Z prefixes for some assets
  if (normalized.startsWith('X') && normalized.length > 3) {
    const withoutX = normalized.substring(1);
    // Check if it's a known crypto
    if (['BTC', 'ETH', 'XRP', 'LTC', 'XMR', 'XLM', 'XDG', 'REP'].includes(withoutX)) {
      normalized = withoutX;
    }
  }
  if (normalized.startsWith('Z') && normalized.length > 3) {
    const withoutZ = normalized.substring(1);
    // Check if it's a fiat
    if (['USD', 'EUR', 'GBP', 'CAD', 'JPY', 'AUD'].includes(withoutZ)) {
      normalized = withoutZ;
    }
  }

  // Common mappings
  const symbolMap: Record<string, string> = {
    'XXBT': 'BTC',
    'XBT': 'BTC',
    'XETH': 'ETH',
    'XXRP': 'XRP',
    'XLTC': 'LTC',
    'XXLM': 'XLM',
    'XXDG': 'DOGE',
    'XDOGE': 'DOGE',
    'ZUSD': 'USD',
    'ZEUR': 'EUR',
    'ZGBP': 'GBP',
  };

  return symbolMap[normalized] || normalized;
}

/**
 * Parse Binance transaction history CSV
 * Format: UTC_Time, Account, Operation, Coin, Change, Remark
 */
function parseBinance(rows: Record<string, string>[]): { transactions: ParsedTransaction[]; errors: string[]; warnings: string[] } {
  const transactions: ParsedTransaction[] = [];
  const errors: string[] = [];
  const warnings: string[] = [];

  // Group by date and operation for aggregation
  const buyOperations = ['buy', 'transaction buy', 'small_assets_exchange_bnb', 'card_purchase'];
  const sellOperations = ['sell', 'transaction sell'];
  const stakingOperations = ['staking rewards', 'eth 2.0 staking rewards', 'launchpool_interest', 'savings_interest', 'pos_savings_interest', 'simple_earn_flexible_interest'];
  const miningOperations = ['mining'];

  for (const row of rows) {
    try {
      const operation = (row.operation || row.type || '').toLowerCase();
      const coin = row.coin || row.asset || '';
      const change = parseFloat(row.change || row.amount || '0');
      const utcTime = row.utc_time || row.time || '';

      if (!coin || !utcTime || isNaN(change)) {
        warnings.push(`Skipping row with missing data: ${JSON.stringify(row)}`);
        continue;
      }

      // Skip fiat currencies
      if (['USD', 'EUR', 'GBP', 'USDT', 'USDC', 'BUSD', 'FDUSD'].includes(coin.toUpperCase())) {
        continue;
      }

      let type: 'buy' | 'sell' | 'mining' | 'staking' | null = null;

      if (buyOperations.some(op => operation.includes(op))) {
        type = 'buy';
      } else if (sellOperations.some(op => operation.includes(op))) {
        type = 'sell';
      } else if (stakingOperations.some(op => operation.includes(op))) {
        type = 'staking';
      } else if (miningOperations.some(op => operation.includes(op))) {
        type = 'mining';
      } else if (operation.includes('deposit') && change > 0) {
        type = 'buy'; // Treat deposits as buys with 0 cost
      }

      if (!type) {
        continue; // Skip unsupported operations
      }

      // Parse date
      const dateObj = new Date(utcTime);
      const date = dateObj.toISOString().split('T')[0];

      transactions.push({
        date,
        type,
        symbol: normalizeSymbol(coin),
        quantity: Math.abs(change),
        price: 0, // Binance doesn't provide price in transaction history
        note: `Binance: ${operation}`,
        exchange: 'binance',
        originalRow: row,
      });
    } catch (err) {
      errors.push(`Error parsing row: ${JSON.stringify(row)} - ${err}`);
    }
  }

  if (transactions.some(t => t.price === 0)) {
    warnings.push('Binance CSV ne contient pas les prix. Vous devrez les ajouter manuellement ou utiliser l\'historique des ordres.');
  }

  return { transactions, errors, warnings };
}

/**
 * Parse Coinbase transaction history CSV
 * Format: Timestamp, Transaction Type, Asset, Quantity Transacted, Spot Price Currency, Spot Price at Transaction, Subtotal, Total, Fees, Notes
 */
function parseCoinbase(rows: Record<string, string>[]): { transactions: ParsedTransaction[]; errors: string[]; warnings: string[] } {
  const transactions: ParsedTransaction[] = [];
  const errors: string[] = [];
  const warnings: string[] = [];

  const buyTypes = ['buy', 'advanced trade buy', 'rewards income', 'learning reward'];
  const sellTypes = ['sell', 'advanced trade sell', 'convert'];
  const stakingTypes = ['staking income', 'rewards income'];

  for (const row of rows) {
    try {
      const transactionType = (row.transaction_type || '').toLowerCase();
      const asset = row.asset || '';
      const quantity = parseFloat(row.quantity_transacted || '0');
      const spotPrice = parseFloat(row.spot_price_at_transaction || row.spot_price || '0');
      const timestamp = row.timestamp || '';
      const total = parseFloat(row.total || row.subtotal || '0');
      const fees = parseFloat(row.fees || row['fees_and/or_spread'] || '0');
      const notes = row.notes || '';

      if (!asset || !timestamp || isNaN(quantity)) {
        warnings.push(`Skipping row with missing data: ${JSON.stringify(row)}`);
        continue;
      }

      // Skip fiat currencies
      if (['USD', 'EUR', 'GBP', 'USDT', 'USDC'].includes(asset.toUpperCase())) {
        continue;
      }

      let type: 'buy' | 'sell' | 'mining' | 'staking' | null = null;

      if (buyTypes.some(bt => transactionType.includes(bt))) {
        type = 'buy';
      } else if (sellTypes.some(st => transactionType.includes(st))) {
        type = 'sell';
      } else if (stakingTypes.some(st => transactionType.includes(st))) {
        type = 'staking';
      } else if (transactionType.includes('receive')) {
        type = 'buy'; // Treat receives as buys
      } else if (transactionType.includes('send')) {
        type = 'sell'; // Treat sends as sells
      }

      if (!type) {
        continue;
      }

      // Parse date
      const dateObj = new Date(timestamp);
      const date = dateObj.toISOString().split('T')[0];

      // Calculate price
      let price = spotPrice;
      if (!price && total && quantity) {
        price = Math.abs(total) / quantity;
      }

      transactions.push({
        date,
        type,
        symbol: normalizeSymbol(asset),
        quantity: Math.abs(quantity),
        price: price || 0,
        total: Math.abs(total),
        fee: fees,
        note: notes ? `Coinbase: ${notes}` : `Coinbase: ${transactionType}`,
        exchange: 'coinbase',
        originalRow: row,
      });
    } catch (err) {
      errors.push(`Error parsing row: ${JSON.stringify(row)} - ${err}`);
    }
  }

  return { transactions, errors, warnings };
}

/**
 * Parse Kraken ledger CSV
 * Format: txid, refid, time, type, subtype, aclass, asset, amount, fee, balance
 */
function parseKraken(rows: Record<string, string>[]): { transactions: ParsedTransaction[]; errors: string[]; warnings: string[] } {
  const transactions: ParsedTransaction[] = [];
  const errors: string[] = [];
  const warnings: string[] = [];

  // Group trades by refid to match buy/sell pairs
  const tradeGroups = new Map<string, Record<string, string>[]>();

  for (const row of rows) {
    const refid = row.refid || '';
    const type = (row.type || '').toLowerCase();

    if (type === 'trade') {
      if (!tradeGroups.has(refid)) {
        tradeGroups.set(refid, []);
      }
      tradeGroups.get(refid)!.push(row);
    } else if (type === 'staking') {
      // Handle staking rewards
      try {
        const asset = row.asset || '';
        const amount = parseFloat(row.amount || '0');
        const time = row.time || '';
        const fee = parseFloat(row.fee || '0');

        if (!asset || !time || isNaN(amount)) continue;

        // Skip fiat
        const symbol = normalizeSymbol(asset);
        if (['USD', 'EUR', 'GBP'].includes(symbol)) continue;

        const dateObj = new Date(time);
        const date = dateObj.toISOString().split('T')[0];

        transactions.push({
          date,
          type: 'staking',
          symbol,
          quantity: Math.abs(amount) - fee,
          price: 0,
          fee,
          note: `Kraken: Staking reward`,
          exchange: 'kraken',
          originalRow: row,
        });
      } catch (err) {
        errors.push(`Error parsing staking row: ${err}`);
      }
    }
  }

  // Process trade pairs
  for (const [refid, tradeRows] of tradeGroups) {
    try {
      if (tradeRows.length < 2) {
        warnings.push(`Incomplete trade pair for refid: ${refid}`);
        continue;
      }

      // Find crypto and fiat sides
      let cryptoRow: Record<string, string> | null = null;
      let fiatRow: Record<string, string> | null = null;

      for (const row of tradeRows) {
        const asset = normalizeSymbol(row.asset || '');
        const amount = parseFloat(row.amount || '0');

        if (['USD', 'EUR', 'GBP', 'USDT', 'USDC'].includes(asset)) {
          fiatRow = row;
        } else if (amount !== 0) {
          cryptoRow = row;
        }
      }

      if (!cryptoRow) {
        // Might be a crypto-to-crypto trade
        const nonZeroRows = tradeRows.filter(r => parseFloat(r.amount || '0') !== 0);
        if (nonZeroRows.length >= 2) {
          // Treat as two separate transactions
          for (const row of nonZeroRows) {
            const asset = normalizeSymbol(row.asset || '');
            const amount = parseFloat(row.amount || '0');
            const time = row.time || '';
            const fee = parseFloat(row.fee || '0');

            if (['USD', 'EUR', 'GBP'].includes(asset)) continue;

            const dateObj = new Date(time);
            const date = dateObj.toISOString().split('T')[0];

            transactions.push({
              date,
              type: amount > 0 ? 'buy' : 'sell',
              symbol: asset,
              quantity: Math.abs(amount) - (amount > 0 ? fee : 0),
              price: 0,
              fee,
              note: `Kraken: Trade (crypto-to-crypto)`,
              exchange: 'kraken',
              originalRow: row,
            });
          }
        }
        continue;
      }

      const asset = normalizeSymbol(cryptoRow.asset || '');
      const cryptoAmount = parseFloat(cryptoRow.amount || '0');
      const time = cryptoRow.time || '';
      const fee = parseFloat(cryptoRow.fee || '0');

      const dateObj = new Date(time);
      const date = dateObj.toISOString().split('T')[0];

      // Calculate price from fiat amount if available
      let price = 0;
      if (fiatRow) {
        const fiatAmount = Math.abs(parseFloat(fiatRow.amount || '0'));
        if (cryptoAmount !== 0) {
          price = fiatAmount / Math.abs(cryptoAmount);
        }
      }

      transactions.push({
        date,
        type: cryptoAmount > 0 ? 'buy' : 'sell',
        symbol: asset,
        quantity: Math.abs(cryptoAmount) - (cryptoAmount > 0 ? fee : 0),
        price,
        fee,
        note: `Kraken: Trade`,
        exchange: 'kraken',
        originalRow: cryptoRow,
      });
    } catch (err) {
      errors.push(`Error parsing trade group ${refid}: ${err}`);
    }
  }

  return { transactions, errors, warnings };
}

/**
 * Parse Crypto.com transaction history CSV
 * Format: Timestamp (UTC), Transaction Description, Currency, Amount, To Currency, To Amount, Native Currency, Native Amount, Native Amount (in USD), Transaction Kind, Transaction Hash
 */
function parseCryptoCom(rows: Record<string, string>[]): { transactions: ParsedTransaction[]; errors: string[]; warnings: string[] } {
  const transactions: ParsedTransaction[] = [];
  const errors: string[] = [];
  const warnings: string[] = [];

  // Transaction types categorization
  const buyTypes = ['crypto_purchase', 'dust_conversion_credited', 'recurring_buy_order', 'card_top_up_reversed', 'crypto_deposit'];
  const sellTypes = ['crypto_withdrawal', 'card_cashback_reverted', 'crypto_to_exchange_transfer', 'dust_conversion_debited'];
  const stakingTypes = ['mco_stake_reward', 'crypto_earn_interest_paid', 'rewards_platform_deposit_credited', 'staking_reward'];
  const airdropTypes = ['referral_card_cashback', 'reimbursement', 'admin_wallet_credited', 'campaign_reward'];

  for (const row of rows) {
    try {
      // Handle different column naming conventions
      const timestamp = row['timestamp_(utc)'] || row.timestamp || row.timestamp_description || '';
      const transactionDescription = (row.transaction_description || row.transaction_kind || '').toLowerCase();
      const currency = row.currency || '';
      const amount = parseFloat(row.amount || '0');
      const toCurrency = row.to_currency || '';
      const toAmount = parseFloat(row.to_amount || '0');
      const nativeAmount = parseFloat(row['native_amount_(in_usd)'] || row.native_amount || '0');

      if (!currency || !timestamp || isNaN(amount)) {
        warnings.push(`Skipping row with missing data: ${JSON.stringify(row)}`);
        continue;
      }

      // Skip fiat currencies and stablecoins for main entries (except staking rewards)
      const isFiat = ['USD', 'EUR', 'GBP', 'USDT', 'USDC', 'DAI', 'TUSD'].includes(currency.toUpperCase());

      let type: 'buy' | 'sell' | 'mining' | 'staking' | null = null;

      if (buyTypes.some(bt => transactionDescription.includes(bt))) {
        type = 'buy';
      } else if (sellTypes.some(st => transactionDescription.includes(st))) {
        type = 'sell';
      } else if (stakingTypes.some(st => transactionDescription.includes(st)) || airdropTypes.some(at => transactionDescription.includes(at))) {
        type = 'staking';
      } else if (transactionDescription.includes('viban_purchase')) {
        // VIBAN purchase: buying crypto with fiat
        type = 'buy';
      } else if (transactionDescription.includes('crypto_exchange')) {
        // Crypto to crypto exchange
        if (amount > 0 && !isFiat) {
          type = 'buy';
        } else if (amount < 0 && !isFiat) {
          type = 'sell';
        }
      } else if (transactionDescription.includes('_deposit')) {
        type = 'buy';
      } else if (transactionDescription.includes('_withdrawal')) {
        type = 'sell';
      }

      if (!type || isFiat) {
        continue;
      }

      // Parse date
      const dateObj = new Date(timestamp);
      const date = dateObj.toISOString().split('T')[0];

      // Calculate price from native amount
      let price = 0;
      if (nativeAmount && amount) {
        price = Math.abs(nativeAmount / amount);
      }

      transactions.push({
        date,
        type,
        symbol: normalizeSymbol(currency),
        quantity: Math.abs(amount),
        price,
        total: Math.abs(nativeAmount),
        note: `Crypto.com: ${transactionDescription}`,
        exchange: 'cryptocom',
        originalRow: row,
      });

      // Handle swap/exchange - add the receiving side
      if (transactionDescription.includes('crypto_exchange') && toCurrency && toAmount > 0) {
        const toCurrencyNorm = normalizeSymbol(toCurrency);
        if (!['USD', 'EUR', 'GBP', 'USDT', 'USDC'].includes(toCurrencyNorm)) {
          transactions.push({
            date,
            type: 'buy',
            symbol: toCurrencyNorm,
            quantity: toAmount,
            price: nativeAmount ? Math.abs(nativeAmount / toAmount) : 0,
            total: Math.abs(nativeAmount),
            note: `Crypto.com: crypto_exchange (received)`,
            exchange: 'cryptocom',
            originalRow: row,
          });
        }
      }
    } catch (err) {
      errors.push(`Error parsing Crypto.com row: ${JSON.stringify(row)} - ${err}`);
    }
  }

  if (transactions.some(t => t.price === 0)) {
    warnings.push('Certaines transactions Crypto.com n\'ont pas de prix. Les valeurs historiques peuvent être nécessaires.');
  }

  return { transactions, errors, warnings };
}

/**
 * Parse KuCoin trade history CSV
 * Format: Time, Trading pair, Side, Price, Amount, Funds, Fee, Fee Currency
 * Alternative: Time, Symbol, Type, Side, Average Price, Price, Amount, Funds, Fee, Fee Currency
 */
function parseKuCoin(rows: Record<string, string>[]): { transactions: ParsedTransaction[]; errors: string[]; warnings: string[] } {
  const transactions: ParsedTransaction[] = [];
  const errors: string[] = [];
  const warnings: string[] = [];

  for (const row of rows) {
    try {
      // Handle different column naming conventions
      const time = row.time || row.order_time || '';
      const tradingPair = row.trading_pair || row.pair || row.symbol || '';
      const side = (row.side || row.type || '').toLowerCase();
      const priceStr = row.price || row.average_price || row.avg_price || '';
      const amountStr = row.amount || row.size || row.quantity || '';
      const fundsStr = row.funds || row.total || row.volume || '';
      const feeStr = row.fee || '0';
      const feeCurrency = row.fee_currency || row.fee_coin || '';

      if (!tradingPair || !time || !side) {
        warnings.push(`Skipping row with missing data: ${JSON.stringify(row)}`);
        continue;
      }

      // Parse trading pair (e.g., "BTC-USDT", "ETH-BTC")
      const pairParts = tradingPair.split('-');
      if (pairParts.length !== 2) {
        warnings.push(`Invalid trading pair format: ${tradingPair}`);
        continue;
      }

      const baseCurrency = pairParts[0]; // BTC in BTC-USDT
      const quoteCurrency = pairParts[1]; // USDT in BTC-USDT

      const price = parseFloat(priceStr) || 0;
      const amount = parseFloat(amountStr) || 0;
      const funds = parseFloat(fundsStr) || (price * amount);
      const fee = parseFloat(feeStr) || 0;

      if (isNaN(amount) || amount === 0) {
        continue;
      }

      // Parse date
      const dateObj = new Date(time);
      const date = dateObj.toISOString().split('T')[0];

      // Determine transaction type
      let type: 'buy' | 'sell';
      let symbol: string;
      let quantity: number;
      let txPrice: number;

      const isQuoteFiat = ['USD', 'EUR', 'GBP', 'USDT', 'USDC', 'BUSD', 'DAI', 'TUSD'].includes(quoteCurrency.toUpperCase());

      if (side === 'buy') {
        // Buying base currency (e.g., buying BTC with USDT)
        type = 'buy';
        symbol = normalizeSymbol(baseCurrency);
        quantity = amount;
        txPrice = price;
      } else if (side === 'sell') {
        // Selling base currency (e.g., selling BTC for USDT)
        type = 'sell';
        symbol = normalizeSymbol(baseCurrency);
        quantity = amount;
        txPrice = price;
      } else {
        // Unknown side, skip
        warnings.push(`Unknown side: ${side}`);
        continue;
      }

      // Skip if it's a fiat/stablecoin trade (base currency is fiat)
      if (['USD', 'EUR', 'GBP'].includes(symbol)) {
        continue;
      }

      // Calculate fee impact
      let feeAmount = 0;
      if (feeCurrency) {
        const normalizedFeeCurrency = normalizeSymbol(feeCurrency);
        if (normalizedFeeCurrency === symbol) {
          feeAmount = fee;
        }
      }

      transactions.push({
        date,
        type,
        symbol,
        quantity: Math.abs(quantity) - (type === 'buy' ? feeAmount : 0),
        price: txPrice,
        total: Math.abs(funds),
        fee: fee,
        note: `KuCoin: ${tradingPair} ${side}`,
        exchange: 'kucoin',
        originalRow: row,
      });

      // For crypto-to-crypto trades (quote is not fiat), add the other side
      if (!isQuoteFiat && !['USD', 'EUR', 'GBP'].includes(quoteCurrency.toUpperCase())) {
        transactions.push({
          date,
          type: side === 'buy' ? 'sell' : 'buy',
          symbol: normalizeSymbol(quoteCurrency),
          quantity: Math.abs(funds),
          price: price > 0 ? 1 / price : 0, // Inverse price
          note: `KuCoin: ${tradingPair} ${side} (counter)`,
          exchange: 'kucoin',
          originalRow: row,
        });
      }
    } catch (err) {
      errors.push(`Error parsing KuCoin row: ${JSON.stringify(row)} - ${err}`);
    }
  }

  return { transactions, errors, warnings };
}

/**
 * Main import function
 */
export async function importCSV(
  file: File,
  exchangeType: ExchangeType = 'auto'
): Promise<ImportResult> {
  const result: ImportResult = {
    success: false,
    transactions: [],
    errors: [],
    warnings: [],
    exchange: exchangeType,
    totalRows: 0,
    importedRows: 0,
    skippedRows: 0,
  };

  try {
    const content = await file.text();
    const { headers, rows } = parseCSV(content);
    result.totalRows = rows.length;

    if (rows.length === 0) {
      result.errors.push('Le fichier CSV est vide ou mal formaté');
      return result;
    }

    // Auto-detect exchange if needed
    if (exchangeType === 'auto') {
      result.exchange = detectExchange(headers);
      if (result.exchange === 'auto') {
        result.errors.push('Impossible de détecter automatiquement l\'exchange. Veuillez sélectionner manuellement.');
        return result;
      }
    }

    // Parse based on exchange type
    let parseResult: { transactions: ParsedTransaction[]; errors: string[]; warnings: string[] };

    switch (result.exchange) {
      case 'binance':
        parseResult = parseBinance(rows);
        break;
      case 'coinbase':
        parseResult = parseCoinbase(rows);
        break;
      case 'kraken':
        parseResult = parseKraken(rows);
        break;
      case 'cryptocom':
        parseResult = parseCryptoCom(rows);
        break;
      case 'kucoin':
        parseResult = parseKuCoin(rows);
        break;
      default:
        result.errors.push(`Exchange non supporté: ${result.exchange}`);
        return result;
    }

    result.transactions = parseResult.transactions;
    result.errors.push(...parseResult.errors);
    result.warnings.push(...parseResult.warnings);
    result.importedRows = result.transactions.length;
    result.skippedRows = result.totalRows - result.importedRows;
    result.success = result.transactions.length > 0;

  } catch (err) {
    result.errors.push(`Erreur lors de la lecture du fichier: ${err}`);
  }

  return result;
}

/**
 * Get coin ID from symbol using CoinGecko search
 */
export async function getCoinIdFromSymbol(symbol: string): Promise<string | null> {
  try {
    // Static import — verified there is no circular dependency back from
    // ./api to this file, and the dynamic import was just defeating Vite's
    // chunking (api.ts is statically imported by 18 other modules).
    const data = await callCoinGeckoAPI('/search', { query: symbol });

    if (data.coins && data.coins.length > 0) {
      // Find exact symbol match
      const exactMatch = data.coins.find(
        (coin: { symbol: string }) => coin.symbol.toLowerCase() === symbol.toLowerCase()
      );
      return exactMatch?.id || data.coins[0].id;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Group transactions by symbol
 */
export function groupTransactionsBySymbol(transactions: ParsedTransaction[]): Map<string, ParsedTransaction[]> {
  const groups = new Map<string, ParsedTransaction[]>();

  for (const tx of transactions) {
    if (!groups.has(tx.symbol)) {
      groups.set(tx.symbol, []);
    }
    groups.get(tx.symbol)!.push(tx);
  }

  return groups;
}

/**
 * Merge transactions into existing portfolio format
 */
export function convertToPositions(transactions: ParsedTransaction[]): {
  tokenId: string;
  type: 'buy' | 'sell' | 'mining' | 'staking';
  date: string;
  quantity: number;
  price: number;
  note?: string;
}[] {
  return transactions.map(tx => ({
    tokenId: tx.symbol.toLowerCase(),
    type: tx.type,
    date: tx.date,
    quantity: tx.quantity,
    price: tx.price,
    note: tx.note,
  }));
}
